import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  Activity, Search, RefreshCw, ShieldCheck, ChevronRight, CalendarDays,
  Clock as ClockIcon, X, AlertTriangle, Trash2, Mail,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import AdminLayout from '../../components/layout/AdminLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import { AppActivityPdfButton } from '../../components/AppActivityPdfButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtAbsolute(iso) {
  if (!iso) return 'Never';
  try { return format(parseISO(iso), 'dd MMM yyyy, HH:mm'); }
  catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy'); }
  catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'HH:mm'); }
  catch { return iso; }
}

export default function AdminAppLoginActivityPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | employee | supervisor
  const [selected, setSelected] = useState(null); // {id, name, email, role}
  const [history, setHistory] = useState(null); // {events:[], shifts:[]}
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/app-login-activity/summary`);
      setRows(r.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows
      .filter(r => roleFilter === 'all' ? true : r.role === roleFilter)
      .filter(r => !s
        || r.name.toLowerCase().includes(s)
        || r.email.toLowerCase().includes(s));
  }, [rows, search, roleFilter]);

  const fetchDetail = useCallback(async (user) => {
    setSelected(user);
    setHistory(null);
    setHistoryLoading(true);
    try {
      const [h, s] = await Promise.all([
        axios.get(`${API}/admin/app-login-activity/${user.id}/history?limit=500`),
        axios.get(`${API}/admin/app-login-activity/${user.id}/shift-check`),
      ]);
      setHistory({ events: h.data.events || [], timeline: h.data.timeline || [], shifts: s.data.shifts || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load login history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const deleteHistory = async () => {
    if (!selected) return;
    try {
      const r = await axios.delete(`${API}/admin/app-login-activity/${selected.id}/history`);
      toast.success(`Deleted ${r.data.deleted} login record${r.data.deleted === 1 ? '' : 's'}`);
      setHistory({ events: [], shifts: history?.shifts || [] });
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete history');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="app-login-activity-page">
      {/* Header */}
      <div className="border-b border-border pb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
            <Activity className="w-6 h-6 text-primary" />
            APP LOGIN ACTIVITY
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Audit trail of every successful sign-in via the login page. Independent of Clock In / Clock Out.
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={fetchSummary}
          disabled={loading}
          data-testid="refresh-activity-btn"
          className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 uppercase tracking-wider text-[11px] gap-2 self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="activity-search-input"
            className="pl-10 bg-background border-border rounded-sm h-10"
          />
        </div>
        <div className="flex items-center gap-2">
          {['all', 'employee', 'supervisor'].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRoleFilter(r)}
              data-testid={`filter-role-${r}`}
              className={[
                'px-3 h-10 rounded-sm border text-xs uppercase tracking-wider font-medium transition-colors',
                roleFilter === r
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border text-muted-foreground hover:text-foreground hover:border-primary/50',
              ].join(' ')}
            >
              {r === 'all' ? 'All' : r === 'employee' ? 'Employees' : 'Supervisors'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border py-4">
          <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground font-medium">
            {filtered.length} {filtered.length === 1 ? 'user' : 'users'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No users match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activity-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-3 font-medium">Employee</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Role</th>
                    <th className="px-4 py-3 font-medium">Last app login</th>
                    <th className="px-4 py-3 font-medium text-right">30 d</th>
                    <th className="px-4 py-3 font-medium text-right">All time</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => fetchDetail(r)}
                      data-testid={`activity-row-${r.id}`}
                      className="border-b border-border/40 hover:bg-primary/5 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{r.name}</div>
                        <ComplianceIndicator userId={r.id} />
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {r.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-border">
                          {r.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-foreground/80">{fmtAbsolute(r.last_app_login)}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        <span className={r.logins_30d > 0 ? 'text-foreground' : 'text-muted-foreground'}>
                          {r.logins_30d}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        <span className={r.logins_all_time > 0 ? 'text-foreground' : 'text-muted-foreground'}>
                          {r.logins_all_time}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent
          side="right"
          className="bg-card border-border w-full sm:max-w-2xl overflow-y-auto"
          data-testid="activity-detail-sheet"
        >
          <SheetHeader className="space-y-1">
            <SheetTitle className="font-barlow tracking-widest uppercase text-primary text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> App login history
            </SheetTitle>
            {selected && (
              <p className="text-sm text-foreground">
                <span className="font-medium">{selected.name}</span>
                <span className="text-muted-foreground"> · {selected.email}</span>
              </p>
            )}
            {selected && (
              <div className="pt-2">
                <AppActivityPdfButton userId={selected.id} userName={selected.name} />
              </div>
            )}
          </SheetHeader>

          {historyLoading && (
            <div className="py-12 text-center text-muted-foreground">Loading history…</div>
          )}

          {!historyLoading && history && (
            <div className="mt-6 space-y-8">
              {/* Login events */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
                    <ClockIcon className="w-3.5 h-3.5" /> Activity timeline ({(history.timeline || history.events).length})
                  </h3>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost" size="sm"
                        data-testid="delete-history-btn"
                        disabled={!history.events.length}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 text-[11px] uppercase tracking-wider gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Clear history
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-destructive" />
                          Delete login history?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently removes <strong>{history.events.length}</strong> login record{history.events.length === 1 ? '' : 's'} for {selected?.name}. New logins will continue to be recorded.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={deleteHistory}
                          data-testid="confirm-delete-history-btn"
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                {(history.timeline || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No login records yet.</p>
                ) : (
                  <ul className="space-y-1 max-h-96 overflow-y-auto pr-2" data-testid="events-list">
                    {(history.timeline || []).map((ev, i) => (
                      <li
                        key={ev.id || i}
                        className={[
                          'px-3 py-2 rounded-sm border',
                          ev.kind === 'login'
                            ? 'border-primary/40 bg-primary/5'
                            : ev.kind === 'clock_in' && ev.ok !== false
                              ? 'border-green-500/50 bg-green-500/10 ml-4'
                              : ev.kind === 'clock_out' && ev.ok !== false
                                ? 'border-blue-500/50 bg-blue-500/10 ml-4'
                                : ev.ok === false
                                  ? 'border-red-500/40 bg-red-500/5 ml-4'
                                  : 'border-border/50 bg-background/50 ml-4',
                        ].join(' ')}
                        data-testid={`timeline-${ev.kind}-${i}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs ${
                            ev.kind === 'login' ? 'font-bold text-primary uppercase tracking-wider'
                            : ev.kind === 'clock_in' && ev.ok !== false ? 'font-bold text-green-400 uppercase tracking-wider'
                            : ev.kind === 'clock_out' && ev.ok !== false ? 'font-bold text-blue-400 uppercase tracking-wider'
                            : ev.ok === false ? 'text-red-400 font-medium' : ''}`}>
                            {ev.kind === 'login' ? '📲 Logged in' : ev.label}
                          </span>
                          <span className="text-xs font-mono whitespace-nowrap">{fmtDate(ev.at)} · <span className="text-primary">{fmtTime(ev.at)}</span></span>
                        </div>
                        {ev.detail ? <p className="text-[10px] text-muted-foreground mt-0.5 break-all">{ev.detail}</p> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Shift check */}
              <section>
                <h3 className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2 mb-3">
                  <CalendarDays className="w-3.5 h-3.5" /> Scheduled shifts — app-login proof
                </h3>
                {history.shifts.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No assigned shifts in the last/next 60 days.</p>
                ) : (
                  <div className="space-y-2" data-testid="shift-check-list">
                    {history.shifts.map(s => (
                      <div
                        key={s.shift_id}
                        className={[
                          'rounded-sm border p-3 transition-colors',
                          s.logged_in_on_shift_date
                            ? 'border-emerald-500/30 bg-emerald-500/5'
                            : 'border-amber-500/30 bg-amber-500/5',
                        ].join(' ')}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">
                              {fmtDate(s.shift_date)}
                              {s.start_time && s.end_time && (
                                <span className="text-muted-foreground font-mono ml-2">
                                  {s.start_time}–{s.end_time}
                                </span>
                              )}
                            </p>
                            {s.event_name && (
                              <p className="text-xs text-muted-foreground mt-0.5">{s.event_name}</p>
                            )}
                          </div>
                          <Badge
                            className={[
                              'text-[10px] uppercase tracking-wider',
                              s.logged_in_on_shift_date
                                ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40'
                                : 'bg-amber-500/15 text-amber-500 border-amber-500/40',
                            ].join(' ')}
                          >
                            {s.logged_in_on_shift_date ? 'Logged in' : 'No login'}
                          </Badge>
                        </div>
                        {s.login_times.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Times: <span className="font-mono text-foreground/80">{s.login_times.join(', ')}</span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
    </AdminLayout>
  );
}
